import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/services/api';
import type { User } from '@/types';

function deleteCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch {
    return null;
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        logout();
      }
    }
    setIsLoading(false);

    // Auto-refresh token if it's close to expiry (within 30 min)
    // or if the remember-me cookie is present
    const rememberMe = getCookie('gps_remember');
    if (token && refreshToken) {
      const payload = decodeJwtPayload(token);
      if (payload && payload.exp) {
        const timeUntilExpiry = payload.exp * 1000 - Date.now();
        const THIRTY_MINUTES = 30 * 60 * 1000;

        if (timeUntilExpiry < THIRTY_MINUTES) {
          // Token expires soon — refresh immediately
          refreshAccessToken(refreshToken);
        } else if (rememberMe) {
          // Token is healthy but remember-me is on — schedule refresh before expiry
          const refreshTimer = setTimeout(
            () => refreshAccessToken(refreshToken),
            Math.max(timeUntilExpiry - THIRTY_MINUTES, 1000)
          );
          return () => clearTimeout(refreshTimer);
        }
      }
    }
  }, []);

  const refreshAccessToken = async (refreshToken: string) => {
    try {
      const response = await authApi.refresh(refreshToken);
      const { access_token } = response.data.data;
      localStorage.setItem('access_token', access_token);
    } catch {
      // Refresh failed — clear everything
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      deleteCookie('gps_remember');
      setUser(null);
      navigate('/login');
    }
  };

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    const { user, tokens } = response.data.data;

    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    localStorage.setItem('user', JSON.stringify(user));

    setUser(user);
    return user;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors on logout
    }

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    deleteCookie('gps_remember');
    setUser(null);
    navigate('/login');
  };

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin ?? false,
    isApprover: user?.is_approver ?? false,
    role: user?.role ?? 'user',
    setUser,
    login,
    logout,
  };
}
